import { CSSProperties, FC, KeyboardEvent, RefObject, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'

import * as Icons from '@icons'
import Button from '@components/atoms/button'
import './demo-walkthrough.css'


type DemoWalkthroughPlacement = 'auto' | 'top' | 'bottom'

export interface DemoWalkthroughStep {
    id: string
    targetId: string
    title: string
    description: string
    placement?: DemoWalkthroughPlacement
    nextLabel?: string
    ctaLabel?: string
    ctaAction?: string
}

export type DemoWalkthroughTargets = Record<string, RefObject<HTMLElement | null>>

interface Props {
    actions?: Record<string, () => void>
    onComplete?: () => void
    onDismissChange?: (dismissed: boolean) => void
    onExit?: () => void
    onStepChange?: (stepId: string) => void
    storageKey: string
    steps: DemoWalkthroughStep[]
    targets: DemoWalkthroughTargets
}

interface PersistedState {
    dismissed?: boolean
    stepId?: string
}

interface SpotlightRect {
    top: number
    left: number
    width: number
    height: number
}

interface PopoverLayout {
    left: number
    top: number
    width: number
}

const DEFAULT_HEADER_OFFSET = 96
const POPOVER_GAP = 16
const POPOVER_WIDTH = 360
const SPOTLIGHT_PADDING = 10
const VIEWPORT_MARGIN = 12

function clamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max)
}

function parseCssLength(value: string) {
    const trimmed = value.trim()
    if (!trimmed) return DEFAULT_HEADER_OFFSET

    if (trimmed.endsWith('rem')) {
        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
        return parseFloat(trimmed) * rootFontSize
    }

    if (trimmed.endsWith('px')) return parseFloat(trimmed)

    return parseFloat(trimmed) || DEFAULT_HEADER_OFFSET
}

function getHeaderOffset() {
    const value = getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    return parseCssLength(value)
}

function readPersistedState(storageKey: string): PersistedState {
    try {
        const rawValue = localStorage.getItem(storageKey)
        if (!rawValue) return {}

        return JSON.parse(rawValue) as PersistedState
    } catch {
        return {}
    }
}

function writePersistedState(storageKey: string, state: PersistedState) {
    try {
        localStorage.setItem(storageKey, JSON.stringify(state))
    } catch {
        // noop
    }
}

function clearPersistedState(storageKey: string) {
    try {
        localStorage.removeItem(storageKey)
    } catch {
        // noop
    }
}

function isTargetRenderable(target: HTMLElement | null) {
    if (!target) return false

    const rect = target.getBoundingClientRect()

    return rect.width > 0 && rect.height > 0
}

const DemoWalkthrough: FC<Props> = ({ actions = {}, onComplete, onDismissChange, onExit, onStepChange, storageKey, steps, targets }) => {
    const navigate = useNavigate()
    const popoverRef = useRef<HTMLDivElement>(null)
    const persistedState = useMemo(() => readPersistedState(storageKey), [storageKey])
    const [currentStepId, setCurrentStepId] = useState(() => persistedState.stepId || steps[0]?.id || '')
    const [dismissed, setDismissed] = useState(() => persistedState.dismissed ?? false)
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)
    const [spotlightRect, setSpotlightRect] = useState<SpotlightRect | null>(null)
    const [popoverLayout, setPopoverLayout] = useState<PopoverLayout | null>(null)

    const currentStepIndex = useMemo(() => {
        const stepIndex = steps.findIndex(step => step.id === currentStepId)
        return stepIndex >= 0 ? stepIndex : 0
    }, [currentStepId, steps])

    const step = steps[currentStepIndex]

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')

        function syncMotionPreference(event?: MediaQueryListEvent) {
            setPrefersReducedMotion(event?.matches ?? mediaQuery.matches)
        }

        syncMotionPreference()
        mediaQuery.addEventListener('change', syncMotionPreference)

        return () => mediaQuery.removeEventListener('change', syncMotionPreference)
    }, [])

    useEffect(() => {
        const nextState = readPersistedState(storageKey)
        setDismissed(nextState.dismissed ?? false)
        setCurrentStepId(steps.some(candidate => candidate.id === nextState.stepId) ? (nextState.stepId || '') : (steps[0]?.id || ''))
    }, [storageKey, steps])

    useEffect(() => {
        if (!step) return

        writePersistedState(storageKey, {
            stepId: step.id,
            dismissed,
        })
    }, [dismissed, step, storageKey])

    useEffect(() => {
        if (!step) return

        onStepChange?.(step.id)
    }, [onStepChange, step])

    useEffect(() => {
        onDismissChange?.(dismissed)
    }, [dismissed, onDismissChange])

    useEffect(() => {
        if (!step || dismissed) return

        const target = targets[step.targetId]?.current
        if (!target || !isTargetRenderable(target)) return

        const headerOffset = getHeaderOffset()
        const rect = target.getBoundingClientRect()
        const topMargin = headerOffset + 16
        const bottomMargin = window.innerHeight - 140
        const isVisible = rect.top >= topMargin && rect.bottom <= bottomMargin

        if (isVisible) return

        target.scrollIntoView({
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
            block: 'center',
        })
    }, [dismissed, prefersReducedMotion, step, targets])

    useEffect(() => {
        if (!step || dismissed) return

        let frameId = 0

        function updateLayout() {
            frameId = window.requestAnimationFrame(() => {
                const target = targets[step.targetId]?.current
                const width = Math.min(POPOVER_WIDTH, window.innerWidth - (VIEWPORT_MARGIN * 2))
                const popoverHeight = popoverRef.current?.offsetHeight || 240

                if (!target || !isTargetRenderable(target)) {
                    setSpotlightRect(null)
                    setPopoverLayout({
                        width,
                        left: clamp((window.innerWidth / 2) - (width / 2), VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN),
                        top: getHeaderOffset() + VIEWPORT_MARGIN,
                    })
                    return
                }

                const headerOffset = getHeaderOffset()
                const rect = target.getBoundingClientRect()
                const spotlightTop = clamp(rect.top - SPOTLIGHT_PADDING, headerOffset + 8, window.innerHeight - 40)
                const spotlightLeft = clamp(rect.left - SPOTLIGHT_PADDING, VIEWPORT_MARGIN, window.innerWidth - 40)
                const spotlightWidth = Math.min(window.innerWidth - (VIEWPORT_MARGIN * 2), rect.width + (SPOTLIGHT_PADDING * 2))
                const spotlightHeight = Math.min(window.innerHeight - (VIEWPORT_MARGIN * 2), rect.height + (SPOTLIGHT_PADDING * 2))
                const preferredPlacement = step.placement || 'auto'
                const belowTop = rect.bottom + POPOVER_GAP
                const aboveTop = rect.top - popoverHeight - POPOVER_GAP
                const canPlaceBelow = belowTop + popoverHeight <= window.innerHeight - VIEWPORT_MARGIN
                const canPlaceAbove = aboveTop >= headerOffset + VIEWPORT_MARGIN

                let placement: Exclude<DemoWalkthroughPlacement, 'auto'> = 'bottom'

                if (preferredPlacement === 'top') placement = canPlaceAbove || !canPlaceBelow ? 'top' : 'bottom'
                else if (preferredPlacement === 'bottom') placement = canPlaceBelow || !canPlaceAbove ? 'bottom' : 'top'
                else placement = canPlaceBelow || !canPlaceAbove ? 'bottom' : 'top'

                setSpotlightRect({
                    top: spotlightTop,
                    left: spotlightLeft,
                    width: spotlightWidth,
                    height: spotlightHeight,
                })

                setPopoverLayout({
                    width,
                    left: clamp(rect.left + (rect.width / 2) - (width / 2), VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN),
                    top: clamp(
                        placement === 'top' ? aboveTop : belowTop,
                        headerOffset + VIEWPORT_MARGIN,
                        window.innerHeight - popoverHeight - VIEWPORT_MARGIN
                    ),
                })
            })
        }

        updateLayout()
        window.addEventListener('resize', updateLayout)
        window.addEventListener('scroll', updateLayout, true)

        return () => {
            window.cancelAnimationFrame(frameId)
            window.removeEventListener('resize', updateLayout)
            window.removeEventListener('scroll', updateLayout, true)
        }
    }, [dismissed, step, targets])

    useEffect(() => {
        if (dismissed) return

        popoverRef.current?.focus()
    }, [dismissed, step])

    useEffect(() => {
        if (step) return

        setSpotlightRect(null)
        setPopoverLayout(null)
    }, [step])

    function handleDismiss() {
        setDismissed(true)
    }

    function handleReopen() {
        setDismissed(false)
    }

    function handleExit() {
        clearPersistedState(storageKey)

        if (onExit) {
            onExit()
            return
        }

        navigate('/account')
    }

    function handleComplete() {
        try {
            localStorage.setItem('demo_completed', 'true')
        } catch {
            // noop
        }

        clearPersistedState(storageKey)

        if (onComplete) {
            onComplete()
            return
        }

        navigate('/group/create')
    }

    function handleNext() {
        if (!step) return
        if (currentStepIndex === steps.length - 1) {
            handleComplete()
            return
        }

        setCurrentStepId(steps[currentStepIndex + 1].id)
    }

    function handleBack() {
        if (currentStepIndex === 0) return

        setCurrentStepId(steps[currentStepIndex - 1].id)
    }

    function handleCtaAction() {
        if (!step?.ctaAction) return

        actions[step.ctaAction]?.()
    }

    function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
        if (event.key === 'Escape') {
            event.preventDefault()
            handleDismiss()
            return
        }

        if (event.key === 'ArrowRight') {
            event.preventDefault()
            handleNext()
            return
        }

        if (event.key === 'ArrowLeft' && currentStepIndex > 0) {
            event.preventDefault()
            handleBack()
        }
    }

    if (!step) return null

    if (dismissed) return (
        <button type="button" className="demo-walkthrough-resume" onClick={handleReopen}>
            <Icons.Info width="1rem" height="1rem" tint="white" />
            Resume Tour
        </button>
    )

    const spotlightStyle = spotlightRect ? {
        top: `${spotlightRect.top}px`,
        left: `${spotlightRect.left}px`,
        width: `${spotlightRect.width}px`,
        height: `${spotlightRect.height}px`,
    } : undefined
    const popoverStyle: CSSProperties | undefined = popoverLayout ? {
        top: `${popoverLayout.top}px`,
        left: `${popoverLayout.left}px`,
        width: `${popoverLayout.width}px`,
    } : undefined

    return (
        <div className="demo-walkthrough-layer" aria-live="polite">
            {spotlightRect && (
                <div
                    aria-hidden="true"
                    className="demo-walkthrough-spotlight"
                    style={spotlightStyle}
                />
            )}

            <AnimatePresence mode="wait">
                <motion.div
                    key={step.id}
                    ref={popoverRef}
                    role="dialog"
                    aria-labelledby={`demo-tour-title-${step.id}`}
                    className="demo-walkthrough-popover"
                    initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: prefersReducedMotion ? 0 : -12 }}
                    transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
                    style={popoverLayout ? { ...popoverStyle, visibility: 'visible' } : { visibility: 'hidden' }}
                    tabIndex={-1}
                    onKeyDown={handleKeyDown}
                >
                    <div className="demo-walkthrough-popover-header">
                        <div className="demo-walkthrough-meta">
                            <p className="demo-walkthrough-label">Demo Tour</p>
                            <div className="demo-walkthrough-dots" aria-hidden="true">
                                {steps.map((tourStep, index) => (
                                    <span
                                        key={tourStep.id}
                                        className={`demo-walkthrough-dot ${index === currentStepIndex ? 'active' : index < currentStepIndex ? 'complete' : ''}`}
                                    />
                                ))}
                            </div>
                        </div>
                        <button type="button" className="demo-walkthrough-close" onClick={handleDismiss} aria-label="Dismiss walkthrough">
                            <Icons.Close width="0.875rem" height="0.875rem" tint="var(--splitcard-black)" />
                        </button>
                    </div>

                    <div className="demo-walkthrough-copy">
                        <p className="demo-walkthrough-progress">Step {currentStepIndex + 1} of {steps.length}</p>
                        <h3 id={`demo-tour-title-${step.id}`}>{step.title}</h3>
                        <p>{step.description}</p>
                    </div>

                    <div className="demo-walkthrough-actions">
                        {currentStepIndex > 0 ? (
                            <button type="button" className="demo-walkthrough-tertiary-action" onClick={handleBack}>
                                Back
                            </button>
                        ) : (
                            <button type="button" className="demo-walkthrough-tertiary-action" onClick={handleExit}>
                                Exit
                            </button>
                        )}

                        {step.ctaLabel && step.ctaAction && (
                            <Button theme="secondary" blueBorder={true} className="demo-walkthrough-cta-action" onClick={handleCtaAction}>
                                {step.ctaLabel}
                            </Button>
                        )}

                        <Button className="demo-walkthrough-next-action" onClick={handleNext}>
                            {step.nextLabel || 'Next'}
                        </Button>
                    </div>
                </motion.div>
            </AnimatePresence>
        </div>
    )
}

export default DemoWalkthrough
